import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@platform/ui-kit';
import './PlatformFitSummary.css';
import { api, type AirFitAnalysis, type AirFitSummaryGroup, type AirFitSummaryOpportunity } from '../../../api';

/**
 * Strategic Intelligence → Platform Fit Summary.
 *
 * Ported from the source's `views/platforms/fit-summary.ejs` and `routes/platform-fit.js`
 * GET /summary.
 *
 * The RESULTS screen. AI Platform Fit runs the analysis; this is where the outcome is read:
 * the best-fit platform per initiative with both score axes and the full six-section
 * justification behind it, grouped by project — and where the chosen platform is ASSIGNED.
 *
 * ── WHAT "BEST FIT" MEANS HERE, IN ORDER ───────────────────────────────────────────────
 *
 * The source resolves it as locked → justified → highest-scoring, and the screen must show
 * WHICH of those it is looking at. An assigned platform is a decision a person made; a
 * justified one is the model's pick; a top-scoring one is only arithmetic. Presenting all
 * three identically would let a suggestion be mistaken for a decision.
 */
export function PlatformFitSummary() {
  const [groups, setGroups] = useState<AirFitSummaryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /* Projects are accordions in the source (`.pfs-proj.open`), collapsed until clicked. */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  /* The opportunity whose full analysis fills the slide-over panel. */
  const [panel, setPanel] = useState<AirFitSummaryOpportunity | null>(null);
  const [exporting, setExporting] = useState<'word' | 'ppt' | null>(null);

  /*
   * The document is generated ONCE per selection and cached server-side, so a second click
   * re-serves the stored bytes — which is why the label flips Generate → Download. The
   * response IS the file, so it is saved via an object URL rather than navigated to: a
   * navigation would lose the session cookie context on some browsers and cannot surface
   * a JSON error body.
   */
  const runExport = async (format: 'word' | 'ppt') => {
    if (!selected.size) return;
    setExporting(format);
    setError(null);
    try {
      const { blob, filename } = await api.aiReadiness.exportFitSummary([...selected], format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      /* The status effect is keyed on `selected`, which has not changed — so refresh it
         directly, or the button would keep saying "Generate" for a document that now
         exists. */
      const d = await api.aiReadiness.getFitExportStatus([...selected]);
      setExportStatus({ analyzed: d.analyzed, covered: d.covered, word: d.word, ppt: d.ppt });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setExporting(null);
    }
  };
  const [exportStatus, setExportStatus] = useState<{
    analyzed: number; covered: number;
    word: { filename: string; size: number } | null;
    ppt: { filename: string; size: number } | null;
  } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.aiReadiness
      .getPlatformFitSummary()
      .then((d) => {
        setGroups(d.projectGroups ?? []);
        setError(null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  /* The toolbar's labels depend on whether a document already exists for EXACTLY this
     selection, so the status is re-read whenever the selection changes. */
  useEffect(() => {
    if (!selected.size) {
      setExportStatus(null);
      return;
    }
    api.aiReadiness
      .getFitExportStatus([...selected])
      .then((d) => setExportStatus({ analyzed: d.analyzed, covered: d.covered, word: d.word, ppt: d.ppt }))
      .catch(() => setExportStatus(null));
  }, [selected]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        opps: g.opps.filter(
          (o) =>
            o.opportunity_id.toLowerCase().includes(q) ||
            o.description.toLowerCase().includes(q) ||
            (o.bestFit ?? '').toLowerCase().includes(q) ||
            g.project_id.toLowerCase().includes(q) ||
            g.description.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.opps.length);
  }, [groups, filter]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const assign = (o: AirFitSummaryOpportunity, platformId: number | null) => {
    /* Changing or clearing an EXISTING assignment requires a justification — the source's
       rule, and the reason the prompt appears only in that case. */
    let justification = '';
    if (o.assigned) {
      const answer = window.prompt(
        'Changing or clearing the assigned platform requires a justification.\n\nWhy is it changing?',
      );
      if (answer === null) return;
      justification = answer.trim();
      if (!justification) {
        setError('A justification is required to change or clear the assigned platform.');
        return;
      }
    }
    setError(null);
    api.aiReadiness
      .assignPlatform({ opportunity_id: o.opportunity_id, platform_id: platformId, justification })
      .then((r) => {
        setNotice(
          r.unchanged
            ? 'No change — that platform is already assigned.'
            : r.assigned
              ? 'Platform assigned to the opportunity.'
              : 'Platform assignment cleared.',
        );
        load();
      })
      .catch((e: Error) => setError(e.message));
  };

  const totalOpps = groups.reduce((n, g) => n + g.opps.length, 0);
  const analysedOpps = groups.reduce((n, g) => n + g.opps.filter((o) => o.analysis).length, 0);

  /* The source shows the FIRST 14 WORDS and a "more" link — fit-summary.ejs:3-4. */
  const words = (t: string | null) => String(t ?? '').trim().split(/\s+/).filter(Boolean);
  const preview = (t: string | null) => {
    const w = words(t);
    return w.slice(0, 14).join(' ') + (w.length > 14 ? '…' : '');
  };

  const open = (pid: string) => expanded.has(pid);
  const toggleOpen = (pid: string) =>
    setExpanded((cur) => {
      const n = new Set(cur);
      if (n.has(pid)) n.delete(pid);
      else n.add(pid);
      return n;
    });

  return (
    <div className="pfs-wrap">
      <PageHeader title="Platform Fit Summary" />

      {/* fit-summary.ejs:88 — filter, count and the two export actions on one row. */}
      <div className="pfs-toolbar">
        <div className="pfs-search">
          <input
            type="text"
            placeholder="Filter by project or opportunity…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <span style={{ flex: 1 }} />
        <span className="pfs-selcount">
          {selected.size} selected · {analysedOpps} of {totalOpps} analyzed
        </span>

        {/*
          fit-summary.ejs:94 — the source's two export actions, in their place and design.
          The label flips Generate → Download once a document exists for the exact current
          selection, which is what `export-status` reports.

          THEY ARE DISABLED, AND DELIBERATELY VISIBLE. `POST /summary/export` and the
          generators behind it (`platformFitDocs.js`, `platformFitNarrative.js`, ~1,200
          lines) are NOT PORTED. Hiding the controls would have made the screen look
          finished; leaving them dead would have been worse. The title says why.
        */}
        <button
          type="button"
          className="custom-btn custom-btn-primary"
          disabled={!selected.size || exporting !== null}
          title={!selected.size ? 'Select at least one analyzed initiative' : undefined}
          onClick={() => void runExport('word')}
        >
          <i className="material-icons" style={{ fontSize: 18 }}>
            description
          </i>
          <span className="lbl">
            {exporting === 'word'
              ? 'Generating…'
              : exportStatus?.word
                ? 'Download Word'
                : 'Generate Word'}
          </span>
        </button>
        <button
          type="button"
          className="custom-btn custom-btn-danger"
          disabled={!selected.size || exporting !== null}
          title={!selected.size ? 'Select at least one analyzed initiative' : undefined}
          onClick={() => void runExport('ppt')}
        >
          <i className="material-icons" style={{ fontSize: 18 }}>
            slideshow
          </i>
          {/* "PPT", not "PowerPoint" — `pfsSetLabel(id, fmt, word)` at fit-summary.ejs:231
              passes the word 'PPT'. */}
          <span className="lbl">
            {exporting === 'ppt' ? 'Generating…' : exportStatus?.ppt ? 'Download PPT' : 'Generate PPT'}
          </span>
        </button>
      </div>

      {error ? <p className="pfs-none">{error}</p> : null}
      {notice ? <p className="pfs-none">{notice}</p> : null}

      {loading ? (
        <p className="pfs-none">Loading…</p>
      ) : !visible.length ? (
        <p className="pfs-none">
          {groups.length
            ? 'No project or opportunity matches that filter.'
            : 'No analyzed opportunities yet. Run an AI Platform Fitness Analysis first.'}
        </p>
      ) : (
        visible.map((g) => {
          const analysed = g.opps.filter((o) => o.analysis).length;
          const all = g.opps.every((o) => selected.has(o.opportunity_id));
          const some = !all && g.opps.some((o) => selected.has(o.opportunity_id));
          return (
            <div className={`pfs-proj${open(g.project_id) ? ' open' : ''}`} key={g.project_id}>
              <div className="pfs-hd" onClick={() => toggleOpen(g.project_id)}>
                <input
                  type="checkbox"
                  className="pfs-proj-chk"
                  checked={all}
                  ref={(el) => {
                    if (el) el.indeterminate = some;
                  }}
                  disabled={!analysed}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    setSelected((s) => {
                      const n = new Set(s);
                      g.opps.forEach((o) =>
                        e.target.checked ? n.add(o.opportunity_id) : n.delete(o.opportunity_id),
                      );
                      return n;
                    })
                  }
                />
                <i className="material-icons caret">chevron_right</i>
                <span className="pid">{g.project_id}</span>
                {g.description ? <span className="pdesc">— {g.description}</span> : null}
                <span className="count">
                  {g.opps.length} initiative{g.opps.length === 1 ? '' : 's'}
                  {analysed ? ` · ${analysed} analyzed` : ''}
                </span>
              </div>

              {/* fit-summary.ejs:59 — fixed layout, declared widths, indented one level. */}
              <div className="pfs-body">
                <table className="pfs-table">
                  <colgroup>
                    <col className="col-sel" />
                    <col className="col-opp" />
                    <col className="col-fit" />
                    <col className="col-expl" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th />
                      <th>Initiative</th>
                      <th>Best fit</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.opps.map((o) => (
                      <tr key={o.opportunity_id}>
                        <td className="pfs-sel">
                          <input
                            type="checkbox"
                            className="pfs-opp-chk"
                            checked={selected.has(o.opportunity_id)}
                            disabled={!o.analysis}
                            onChange={() => toggle(o.opportunity_id)}
                          />
                        </td>
                        <td className="pfs-opp">
                          <div className="oid">{o.opportunity_id}</div>
                          {o.description ? <div className="odesc">{o.description}</div> : null}
                        </td>
                        <td>
                          {o.bestFit ? (
                            <>
                              <span className="pfs-fit">
                                {o.bestFit}
                                {o.assigned ? <span className="assigned">ASSIGNED</span> : null}
                              </span>
                              {o.overallScore != null ||
                              o.fitScore != null ||
                              o.readinessScore != null ? (
                                <div className="pfs-scores">
                                  {o.overallScore != null ? (
                                    <span className="sc all">Overall {o.overallScore}</span>
                                  ) : null}
                                  {o.fitScore != null ? (
                                    <span className="sc fit">Fit {o.fitScore}</span>
                                  ) : null}
                                  {o.readinessScore != null ? (
                                    <span className="sc rdy">Ready {o.readinessScore}</span>
                                  ) : null}
                                </div>
                              ) : null}
                              <div style={{ marginTop: 6 }}>
                                <button
                                  type="button"
                                  className="custom-btn custom-btn-secondary"
                                  style={{ height: 28, fontSize: 12 }}
                                  disabled={!o.assigned && o.bestFitPlatformId === null}
                                  onClick={() =>
                                    assign(o, o.assigned ? null : o.bestFitPlatformId)
                                  }
                                >
                                  {o.assigned ? 'Unassign' : 'Assign'}
                                </button>
                              </div>
                            </>
                          ) : (
                            <span className="pfs-none">Not analyzed</span>
                          )}
                        </td>
                        <td className="pfs-expl">
                          {o.explanation ? (
                            <>
                              <span className="just-preview">{preview(o.explanation)}</span>{' '}
                              {o.analysis ? (
                                <a className="just-more" onClick={() => setPanel(o)}>
                                  more
                                </a>
                              ) : null}
                            </>
                          ) : (
                            <span className="pfs-none">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      {/* ── the 560px slide-over holding the SIX-SECTION analysis — fit-summary.ejs:205 ── */}
      <div className={`pfs-overlay${panel ? ' on' : ''}`} onClick={() => setPanel(null)} />
      <aside className={`pfs-panel${panel ? ' open' : ''}`} aria-label="Full justification">
        <div className="pfs-panel-hd">
          <span className="t">
            {panel ? `${panel.opportunity_id} — ${panel.bestFit ?? ''}` : 'Justification'}
          </span>
          <button type="button" onClick={() => setPanel(null)} aria-label="Close">
            ×
          </button>
        </div>
        <div className="pfs-panel-body">{panel?.analysis ? <Analysis a={panel.analysis} /> : null}</div>
      </aside>
    </div>
  );
}

/**
 * The full six-section analysis, in the source's own order and wording —
 * `renderAnalysis()` at fit-summary.ejs:7-44. The source builds an HTML string and escapes
 * every value; here JSX escapes by construction, so the structure is reproduced directly
 * rather than through `dangerouslySetInnerHTML`.
 */
function Analysis({ a }: { a: AirFitAnalysis }) {
  const cat = (title: string, arr?: { category: string; points: string[] }[]) =>
    Array.isArray(arr) && arr.length ? (
      <>
        <h4>{title}</h4>
        {arr.map((g, i) => (
          <div key={i}>
            <div className="ja-cat">{g.category}</div>
            <ul className="ja-list">
              {(g.points ?? []).map((p, j) => (
                <li key={j}>{p}</li>
              ))}
            </ul>
          </div>
        ))}
      </>
    ) : null;

  return (
    <>
      {a.executive_summary ? (
        <>
          <h4>Executive summary</h4>
          <p>{a.executive_summary}</p>
        </>
      ) : null}

      {a.business_drivers?.length ? (
        <>
          <h4>Business drivers</h4>
          <table className="ja-tbl">
            <thead>
              <tr>
                <th>Requirement</th>
                <th>Importance</th>
                <th>Assessment</th>
              </tr>
            </thead>
            <tbody>
              {a.business_drivers.map((d, i) => (
                <tr key={i}>
                  <td>{d.requirement}</td>
                  <td>{d.importance}</td>
                  <td>{d.assessment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {a.measurable ? (
        <>
          <h4>Measurable justification</h4>
          {a.measurable.rows?.length ? (
            <table className="ja-tbl">
              <thead>
                <tr>
                  <th>Area</th>
                  <th>Priority</th>
                  <th>Score</th>
                  <th>Justification</th>
                </tr>
              </thead>
              <tbody>
                {a.measurable.rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.area}</td>
                    <td>{r.importance}</td>
                    <td>{r.score}</td>
                    <td>{r.justification}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {a.measurable.narrative ? <p>{a.measurable.narrative}</p> : null}
        </>
      ) : null}

      {cat('Strengths', a.strengths)}
      {cat('Limitations', a.limitations)}

      {a.risks?.length ? (
        <>
          <h4>Business risks</h4>
          <table className="ja-tbl">
            <thead>
              <tr>
                <th>Risk</th>
                <th>Impact</th>
                <th>Mitigation</th>
              </tr>
            </thead>
            <tbody>
              {a.risks.map((r, i) => (
                <tr key={i}>
                  <td>{r.risk}</td>
                  <td>{r.impact}</td>
                  <td>{r.mitigation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {a.adoption_considerations ? (
        <>
          <h4>Adoption considerations</h4>
          <p>{a.adoption_considerations}</p>
        </>
      ) : null}
    </>
  );
}
